import React, { useEffect, useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Folder,
  FileText,
  Download,
  Link2,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Clock,
  Tag,
  Eye,
  X,
  MailQuestion,
  Lock,
  Globe,
  StickyNote,
  CheckCircle2,
  AlertTriangle,
  CalendarClock,
  Sparkles,
  Wand2,
  Loader2,
  ExternalLink,
  TrendingUp,
  Tags,
  Info,
} from "lucide-react";
import { api, API } from "../lib/api";
import { PagedMatches } from "./Preventivatore";
import { AssetCoverThumbnail } from "../components/AssetCoverThumbnail";
import { useAuth } from "../context/AuthContext";
import DownloadDisclaimerModal from "../components/DownloadDisclaimerModal";

const VAT_RATE = 0.22;
function stripVat(amount) {
  return Math.round(Number(amount || 0) / (1 + VAT_RATE));
}
const fmtEUR = (n) => `€ ${Math.round(Number(n) || 0).toLocaleString("it-IT")}`;

/**
 * Stato del "termine ordine per consegne" per studio/trader.
 */
function getOrderDeadlineAlert(supplier) {
  const dateStr = supplier?.order_deadline_date || "";
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (days === 0) return { state: "today", days: 0, date: target };
  if (days > 0 && days <= 15) return { state: "imminent", days, date: target };
  if (days > 15) return { state: "future", days, date: target };
  return { state: "past", days: -days, date: target };
}

/**
 * Restituisce la chiave del tipo di chiusura ("summer" | "winter" | null) per la data target.
 */
function getClosureKey(date) {
  if (!date) return null;
  const m = date.getMonth();
  const d = date.getDate();
  if (m < 7 || (m === 7 && d <= 15)) return "summer";
  if (m >= 9) return "winter";
  return null;
}

const KIND_GROUP_VALUES = ["catalogo", "listino", "finiture", "cad_3d", "altro"];

/**
 * Magic Search — ricerca testuale full-text dentro tutti i PDF di un fornitore.
 * Mostra i risultati con file + pagina + snippet evidenziato, e all'apertura
 * porta il PDF direttamente alla pagina giusta (#page=N).
 */
function highlightSnippet(text, q) {
  if (!q) return text;
  // Tokenizza per supportare AND multi-parola (lo backend già fa AND; qui evidenziamo
  // tutti i token presenti).
  const tokens = String(q).split(/\s+/).filter(Boolean).slice(0, 5);
  if (tokens.length === 0) return text;
  const escaped = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "ig");
  const parts = String(text).split(re);
  return parts.map((p, i) => {
    const lower = p.toLowerCase();
    const isMatch = tokens.some((t) => t.toLowerCase() === lower);
    return isMatch ? (
      <mark key={i} className="bg-gold/40 text-ink px-0.5 rounded-sm not-italic font-medium">
        {p}
      </mark>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    );
  });
}

export function MagicSearch({ supplierId, assets, onOpenAtPage, onOpenFile, initialQuery = "" }) {
  const { t } = useTranslation();
  const [q, setQ] = useState(initialQuery || "");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Refs per scroll automatico quando arriva initialQuery da deep-link
  const panelRef = React.useRef(null);
  const inputRef = React.useRef(null);

  // Quando cambia il supplier (deep-link da Top10) sincronizziamo la query iniziale
  // e portiamo il pannello in vista con focus sull'input.
  useEffect(() => {
    if (initialQuery) {
      setQ(initialQuery);
      // Scroll + focus dopo il render del nuovo supplier
      const id = setTimeout(() => {
        try {
          panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          inputRef.current?.focus();
          inputRef.current?.select();
        } catch (_) { /* ignore */ }
      }, 120);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [supplierId, initialQuery]);

  // Stato indicizzazione (basato sugli asset PDF visibili: ricavato da `assets`)
  const indexStats = useMemo(() => {
    const pdfs = assets.filter(
      (a) => !a.archived && (
        (a.content_type || "").toLowerCase().includes("pdf") ||
        (a.filename || "").toLowerCase().endsWith(".pdf"))
    );
    const indexed = pdfs.filter((a) => a.indexed_status === "indexed").length;
    return { total: pdfs.length, indexed };
  }, [assets]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      return undefined;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`/suppliers/${supplierId}/assets/search`, {
          params: { q: term },
          signal: ctrl.signal,
        });
        setResults(r.data);
      } catch (err) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setError(t("suppliers.magic_search_error"));
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [q, supplierId, t]);

  // Quando l'input torna corto, scartiamo i risultati precedenti DURANTE il render
  // (evita il pattern set-state-in-effect e mantiene la UI coerente).
  const term = q.trim();
  const effectiveResults = term.length < 2 ? null : results;
  const effectiveError = term.length < 2 ? "" : error;
  const showIndexingHint =
    indexStats.total > 0 && indexStats.indexed < indexStats.total;

  return (
    <div
      ref={panelRef}
      className="relative overflow-hidden mb-6 rounded-sm border border-gold/60 bg-cream shadow-[0_4px_18px_-8px_rgba(176,138,82,0.35)]"
      data-testid="magic-search-panel"
    >
      {/* Accent decorativi soft */}
      <div className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full bg-gold/15 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-8 h-28 w-28 rounded-full bg-gold/10 blur-2xl" />

      <div className="relative p-5 lg:p-6">
        <div className="flex items-center gap-3">
          <Wand2 size={18} className="text-gold animate-pulse" />
          <div className="font-display text-2xl tracking-[0.15em] text-ink">
            <span className="text-gold">MAGIC</span>{" "}
            <span className="bg-gradient-to-r from-gold via-amber-500 to-gold bg-clip-text text-transparent">
              SEARCH
            </span>
          </div>
          <Sparkles size={14} className="text-gold/80" />
        </div>
        <p className="mt-1.5 text-[12px] text-slate1 leading-relaxed max-w-xl">
          {t("suppliers.magic_search_subtitle")}
        </p>
        <p className="mt-0.5 text-[10px] tracking-[0.12em] uppercase text-gold/80">
          {t("suppliers.magic_search_and_hint")}
        </p>

        <div className="mt-4 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gold" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("suppliers.magic_search_placeholder")}
            className="w-full pl-11 pr-24 py-3.5 bg-white border border-gold/40 text-ink placeholder-slate1/60 rounded-sm text-sm focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all"
            data-testid="magic-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {loading && <Loader2 size={14} className="text-gold animate-spin" />}
            {q && (
              <button
                onClick={() => setQ("")}
                className="text-slate1 hover:text-ink text-[10px] tracking-[0.2em] uppercase"
                data-testid="magic-search-clear"
                aria-label={t("suppliers.magic_search_clear")}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {showIndexingHint && (
          <div className="mt-3 text-[11px] text-gold-dark flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" />
            {t("suppliers.magic_search_indexing_hint", {
              indexed: indexStats.indexed,
              total: indexStats.total,
            })}
          </div>
        )}

        {/* Risultati */}
        {term.length >= 2 && (
          <div className="mt-5" data-testid="magic-search-results">
            {effectiveError && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-300 rounded-sm p-3">
                {effectiveError}
              </div>
            )}
            {!effectiveError && effectiveResults && effectiveResults.results.length === 0 && !loading && (
              <div className="text-sm text-slate1 bg-white border border-ink/15 rounded-sm p-4">
                {t("suppliers.magic_search_empty", { q: term })}
              </div>
            )}
            {!effectiveError && effectiveResults && effectiveResults.results.length > 0 && (
              <>
                <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-2 font-medium">
                  {t("suppliers.magic_search_total", { count: effectiveResults.total_matches })} ·{" "}
                  {t("suppliers.magic_search_in_n_files", { count: effectiveResults.results.length })}
                </div>
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {effectiveResults.results.map((r) => (
                    <div
                      key={r.asset_id}
                      className="bg-white border border-ink/15 rounded-sm p-3 hover:border-gold/60 hover:shadow-sm transition-all"
                      data-testid={`magic-result-${r.asset_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 min-w-0">
                          <FileText size={14} className="text-gold mt-0.5 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-ink truncate font-medium">
                              {highlightSnippet(r.title, term)}
                            </div>
                            {r.title_match && (
                              <div className="text-[10px] tracking-[0.18em] uppercase text-gold mt-0.5">
                                · {t("suppliers.magic_search_title_match")}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => onOpenFile && onOpenFile(r, term)}
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-slate1 hover:text-gold transition-colors"
                          data-testid={`magic-open-file-${r.asset_id}`}
                        >
                          <ExternalLink size={11} /> {t("suppliers.magic_search_open_file")}
                        </button>
                      </div>
                      {r.matches && r.matches.length > 0 && (
                        <PagedMatches
                          items={r.matches}
                          wrapperClassName="mt-2 space-y-1.5"
                          testidPrefix={`magic-supplier-result-${r.asset_id}`}
                          renderItem={(m, idx) => (
                            <button
                              key={idx}
                              onClick={() => onOpenAtPage && onOpenAtPage(r, m.page, term)}
                              className="block w-full text-left bg-cream/60 border border-ink/10 hover:border-gold hover:bg-cream rounded-sm px-3 py-2 transition-colors group"
                              data-testid={`magic-open-page-${r.asset_id}-${m.page}`}
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-[10px] tracking-[0.2em] uppercase text-gold font-medium">
                                  {t("suppliers.magic_search_open_page", { page: m.page })}
                                </span>
                                <ExternalLink
                                  size={11}
                                  className="text-slate1/50 group-hover:text-gold"
                                />
                              </div>
                              <div className="text-[12px] leading-relaxed text-ink/80 italic">
                                {highlightSnippet(m.snippet, term)}
                              </div>
                            </button>
                          )}
                        />
                      )}
                      {/* Google Drive folder: lista file con nome corrispondente al termine */}
                      {r.is_gdrive_folder && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-gold mb-1.5">
                            <Folder size={11} />
                            Cartella Google Drive · {r.gdrive_files_count} file totali
                            {r.gdrive_matches_count > 0 && <> · {r.gdrive_matches_count} match</>}
                          </div>
                          {r.gdrive_matches && r.gdrive_matches.length > 0 && (
                            <div className="space-y-1.5">
                              {r.gdrive_matches.map((gf, gidx) => (
                                <a
                                  key={gidx}
                                  href={gf.web_view_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block bg-cream/60 border border-ink/10 hover:border-gold hover:bg-cream rounded-sm px-3 py-2 transition-colors group"
                                  data-testid={`magic-gdrive-file-${r.asset_id}-${gidx}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                      {gf.icon_link ? (
                                        <img src={gf.icon_link} alt="" className="w-3.5 h-3.5 shrink-0" />
                                      ) : (
                                        <FileText size={11} className="text-slate1 shrink-0" />
                                      )}
                                      <span className="text-[12px] text-ink truncate">
                                        {highlightSnippet(gf.name, term)}
                                      </span>
                                    </div>
                                    <ExternalLink size={11} className="text-slate1/50 group-hover:text-gold shrink-0" />
                                  </div>
                                </a>
                              ))}
                            </div>
                          )}
                          {(!r.gdrive_matches || r.gdrive_matches.length === 0) && r.external_url && (
                            <a
                              href={r.external_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-slate1 hover:text-gold transition-colors"
                            >
                              <ExternalLink size={11} /> Apri cartella su Drive
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function isPreviewable(a) {
  if (a.content_type === "external/url") return false;
  const ct = (a.content_type || "").toLowerCase();
  const fn = (a.filename || "").toLowerCase();
  if (ct === "application/pdf" || fn.endsWith(".pdf")) return true;
  if (ct.startsWith("image/")) return true;
  return false;
}

function formatWebsite(url) {
  return (url || "").startsWith("http") ? url : `https://${url}`;
}

/**
 * Mini-vetrina "Prodotti outlet dello stesso marchio".
 * Visibile in basso a destra nella scheda fornitore per i ruoli Trader/Studio.
 * Mostra fino a 3 prodotti dello stesso brand con: foto · nome · listino (IVA inclusa)
 * · netto riservato (IVA scorporata). Sotto, link "Vedi tutti N prodotti".
 * Se nessun prodotto del brand è in outlet, il componente non viene renderizzato.
 */
export function OutletMatchCard({ supplierId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [products, setProducts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [brand, setBrand] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);

  const accountType = user?.account_type;
  const role = user?.role;
  // Visibile solo a trader e studio (architetti) loggati, NON admin.
  const eligible = role !== "admin" && (accountType === "trader" || accountType === "studio");

  React.useEffect(() => {
    if (!supplierId || !eligible) return;
    let alive = true;
    api.get(`/suppliers/${supplierId}/outlet-product`)
      .then((r) => {
        if (!alive) return;
        const data = r.data || {};
        // Backend ritorna {products: [], total, brand, product} con retro-compat.
        const arr = Array.isArray(data.products) ? data.products : (data.product ? [data.product] : []);
        setProducts(arr);
        setTotal(Number(data.total || arr.length));
        setBrand(data.brand || "");
        setLoaded(true);
      })
      .catch(() => { if (alive) { setProducts([]); setTotal(0); setLoaded(true); } });
    return () => { alive = false; };
  }, [supplierId, eligible]);

  if (!eligible || !loaded || products.length === 0) return null;

  // Calcolo netto riservato per un prodotto in base al ruolo
  function computeNetto(p) {
    const listinoLordo = Number(p.price_retail || 0);
    const userPct = Number(p.user_discount_pct || 0);
    if (accountType === "trader" && p.price_trader != null) {
      return stripVat(p.price_trader);
    }
    if (p.price_offer != null) {
      return stripVat(p.price_offer);
    }
    if (userPct > 0 && listinoLordo > 0) {
      return stripVat(listinoLordo * (1 - userPct / 100));
    }
    return null;
  }

  const resolveImg = (p) => p.image_url
    ? (p.image_url.startsWith("http") ? p.image_url : `${process.env.REACT_APP_BACKEND_URL}${p.image_url}`)
    : "";

  const reservedLbl = accountType === "trader"
    ? t("suppliers.outlet_match_reserved_trader")
    : t("suppliers.outlet_match_reserved_studio");

  // Link "Vedi tutti": apre l'outlet pre-filtrato sul brand (`?brand=<name>`).
  const seeAllHref = brand
    ? `/outlet?brand=${encodeURIComponent(brand)}`
    : "/outlet";

  const remaining = Math.max(0, total - products.length);

  return (
    <div
      className="bg-cream2 border border-gold/40 rounded-sm p-4"
      data-testid="supplier-outlet-match"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase text-gold flex items-center gap-1.5 mb-3">
        <Tags size={11} /> {t("suppliers.outlet_match_section")}
      </div>

      <div className="space-y-3">
        {products.map((p, idx) => {
          const imgSrc = resolveImg(p);
          const listino = Number(p.price_retail || 0);
          const netto = computeNetto(p);
          return (
            <Link
              key={p.id || idx}
              to="/outlet"
              className="flex gap-3 group hover:bg-cream rounded-sm -mx-1 px-1 py-1 transition-colors"
              data-testid={`supplier-outlet-match-item-${idx}`}
            >
              <div className="w-16 h-16 flex-shrink-0 bg-cream rounded-sm overflow-hidden border border-ink/10">
                {imgSrc ? (
                  <img src={imgSrc} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate1 text-[9px]">—</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm text-ink leading-tight truncate" title={p.name}>{p.name}</div>
                {p.category && (
                  <div className="text-[9px] tracking-[0.15em] uppercase text-slate1 mt-0.5">{p.category}</div>
                )}
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex items-baseline justify-between gap-2 text-[11px]">
                    <span className="text-slate1">{t("suppliers.outlet_match_listino")}</span>
                    <span className="text-ink font-medium tabular-nums">{fmtEUR(listino)}</span>
                  </div>
                  {netto != null && (
                    <div className="flex items-baseline justify-between gap-2 text-[11px]">
                      <span className="text-gold tracking-wide truncate">{reservedLbl}</span>
                      <span className="text-gold font-display text-sm tabular-nums">{fmtEUR(netto)}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      <Link
        to={seeAllHref}
        className="mt-4 pt-3 border-t border-gold/20 flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-slate1 hover:text-ink transition-colors"
        data-testid="supplier-outlet-match-cta"
      >
        <span>
          {remaining > 0
            ? t("suppliers.outlet_match_see_all_n", { count: total })
            : t("suppliers.outlet_match_cta")}
        </span>
        <span>→</span>
      </Link>
    </div>
  );
}

function SupplierProfilePanel({ supplier }) {
  const { t, i18n } = useTranslation();
  const s = supplier || {};
  const chain = (s.discount_chain || []).filter((v) => Number(v) > 0);
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";

  // Carica anteprima sito (screenshot + 2-3 immagini casuali) lazy.
  // L'endpoint cache 30gg in DB, quindi la prima visita scarica e le successive
  // sono istantanee. Random sample lato server per varietà ad ogni apertura.
  const [sitePreview, setSitePreview] = React.useState(null);
  React.useEffect(() => {
    if (!s.id || !s.website) {
      setSitePreview(null);
      return;
    }
    let alive = true;
    api.get(`/suppliers/${s.id}/site-preview`)
      .then((r) => { if (alive) setSitePreview(r.data); })
      .catch(() => { if (alive) setSitePreview({ images: [], screenshot_url: "", website_url: "" }); });
    return () => { alive = false; };
  }, [s.id, s.website]);

  return (
    <div className="space-y-5" data-testid="supplier-profile">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-slate1">{t("suppliers.profile_section")}</div>
        <h2 className="mt-1 font-display text-3xl text-ink leading-tight">{s.name}</h2>
        {s.description && (
          <p className="mt-3 text-sm text-slate1 leading-relaxed">{s.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-cream2 border border-ink/10 rounded-sm p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 flex items-center gap-1.5">
            <Clock size={11} /> {t("suppliers.lead_time")}
          </div>
          <div className="mt-1 font-display text-xl text-ink">
            {s.lead_time_days > 0 ? t("suppliers.lead_time_days_full", { days: s.lead_time_days }) : t("suppliers.lead_time_not_specified")}
          </div>
        </div>
        <div className="bg-cream2 border border-ink/10 rounded-sm p-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 flex items-center gap-1.5">
            <Globe size={11} /> {t("suppliers.website_lbl")}
          </div>
          <div className="mt-1">
            {s.website ? (
              <a
                href={formatWebsite(s.website)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink hover:text-gold underline underline-offset-4 break-all text-sm"
                data-testid="supplier-website-link"
              >
                {s.website.replace(/^https?:\/\//, "")}
              </a>
            ) : (
              <span className="text-sm text-slate1">—</span>
            )}
          </div>
          {/* Anteprima homepage del sito + 2-3 immagini casuali scraped (cache 30gg) */}
          {s.website && sitePreview && (sitePreview.screenshot_url || sitePreview.images?.length > 0) && (
            <div className="mt-3 space-y-2" data-testid="supplier-site-preview">
              {sitePreview.screenshot_url && (
                <a
                  href={sitePreview.website_url || formatWebsite(s.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group relative overflow-hidden rounded-sm border border-ink/15 hover:border-gold transition-colors"
                  title={t("suppliers.site_preview_open")}
                  data-testid="supplier-site-screenshot"
                >
                  <img
                    src={sitePreview.screenshot_url}
                    alt="Anteprima homepage"
                    loading="lazy"
                    className="w-full h-32 object-cover object-top group-hover:opacity-90 transition-opacity"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <span className="absolute bottom-1 right-1 bg-ink/80 text-cream text-[9px] tracking-[0.2em] px-2 py-0.5 uppercase opacity-0 group-hover:opacity-100 transition-opacity">
                    {t("suppliers.site_preview_open_short")}
                  </span>
                </a>
              )}
              {sitePreview.images && sitePreview.images.length > 0 && (
                <div className="grid grid-cols-3 gap-1.5" data-testid="supplier-site-images">
                  {sitePreview.images.slice(0, 3).map((src, i) => (
                    <a
                      key={i}
                      href={sitePreview.website_url || formatWebsite(s.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-square overflow-hidden rounded-sm border border-ink/10 hover:border-gold transition-colors bg-cream"
                      data-testid={`supplier-site-image-${i}`}
                    >
                      <img
                        src={src}
                        alt={`Immagine ${i + 1} dal sito ${s.name}`}
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {(() => {
        const od = getOrderDeadlineAlert(s);
        if (!od) return null;
        const cls = {
          today:    "bg-rose-50 border-rose-300",
          imminent: "bg-rose-50 border-rose-300",
          future:   "bg-blue-50 border-blue-200",
          past:     "bg-slate-100 border-slate-300",
        };
        const accent = {
          today:    "text-rose-800",
          imminent: "text-rose-800",
          future:   "text-blue-800",
          past:     "text-slate-600",
        };
        const closureKey = getClosureKey(od.date);
        const closureLabel = closureKey
          ? t(`suppliers.closure_${closureKey}`)
          : null;
        const tail = closureLabel
          ? t("suppliers.closure_before", { closure: closureLabel })
          : t("suppliers.closure_default");
        const dateStr = od.date.toLocaleDateString(dateLocale);
        const msg =
          od.state === "today" ? t("suppliers.deadline_msg_today", { tail })
          : od.state === "imminent" ? (od.days === 1
              ? t("suppliers.deadline_msg_last_day", { tail })
              : t("suppliers.deadline_msg_imminent", { count: od.days, tail }))
          : od.state === "future" ? t("suppliers.deadline_msg_future", { tail, date: dateStr })
          : t("suppliers.deadline_msg_past", { date: dateStr });
        return (
          <div className={`border rounded-sm p-4 ${cls[od.state]}`} data-testid="sup-profile-deadline">
            <div className={`text-[10px] tracking-[0.2em] uppercase flex items-center gap-1.5 ${accent[od.state]}`}>
              {(od.state === "today" || od.state === "imminent") ? <AlertTriangle size={11} /> : <CalendarClock size={11} />}
              {t("suppliers.deadline_order")}
            </div>
            <div className={`mt-1 font-display text-base ${accent[od.state]}`}>
              {msg}
            </div>
          </div>
        );
      })()}

      <div className="bg-white border border-ink/10 rounded-sm p-4">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gold flex items-center gap-1.5">
          <Tag size={11} /> {t("suppliers.reserved_conditions")}
        </div>
        {s.has_access ? (
          <>
            <div className="mt-2 font-display text-2xl text-ink">
              {chain.length > 1
                ? `${chain.join(" + ")} `
                : ""}
              <span className="text-gold">{s.discount_pct}%</span>
            </div>
            {chain.length > 1 && (
              <div className="text-[11px] tracking-[0.15em] uppercase text-slate1 mt-0.5">
                {t("suppliers.reserved_chain_label")}
              </div>
            )}
          </>
        ) : (
          <div className="mt-2 text-sm text-slate1">
            {t("suppliers.reserved_not_yet")}
          </div>
        )}
      </div>

      {s.has_access && s.admin_note && (
        <div className="bg-gold/5 border border-gold/30 rounded-sm p-4" data-testid="supplier-admin-note">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold flex items-center gap-1.5">
            <StickyNote size={11} /> {t("suppliers.referent_notes")}
          </div>
          <p className="mt-2 text-sm text-ink whitespace-pre-wrap leading-relaxed">
            {s.admin_note}
          </p>
        </div>
      )}

      {/* Mini-card prodotto outlet dello stesso brand (solo Trader/Studio) */}
      <OutletMatchCard supplierId={s.id} />
    </div>
  );
}

function AssetList({ supplier, assets, aQ, setAQ, onRequestAccess, onPreview, onDownload, previewLoading, newSince, initialMagicQuery = "" }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";

  // Magic Search handlers (apri PDF a pagina specifica oppure file completo).
  // `term` propaga il termine cercato per attivare l'highlight visivo nel viewer PDF.
  const handleOpenAtPage = (result, page, term) => {
    if (result.is_external && result.external_url) {
      window.open(result.external_url, "_blank");
      return;
    }
    if (result.is_gdrive_folder && result.external_url) {
      window.open(result.external_url, "_blank");
      return;
    }
    const asset = assets.find((a) => a.id === result.asset_id);
    if (asset) onPreview(asset, page, term);
  };
  const handleOpenFile = (result, term) => handleOpenAtPage(result, undefined, term);

  // Badge "Nuovo" / "Aggiornato" rispetto all'ultima apertura della scheda fornitore
  const isNewAsset = (a) => Boolean(newSince && a.created_at && a.created_at > newSince);
  const isUpdatedAsset = (a) => Boolean(newSince && !isNewAsset(a) && a.updated_at && a.updated_at > newSince);

  const freshCounts = useMemo(() => {
    let added = 0;
    let updated = 0;
    assets.forEach((a) => {
      if (newSince && a.created_at && a.created_at > newSince) added += 1;
      else if (newSince && a.updated_at && a.updated_at > newSince) updated += 1;
    });
    return { added, updated };
  }, [assets, newSince]);

  const filteredAssets = useMemo(() => {
    if (!aQ) return assets;
    const k = aQ.toLowerCase();
    return assets.filter(
      (a) =>
        (a.title || "").toLowerCase().includes(k) ||
        (a.filename || "").toLowerCase().includes(k)
    );
  }, [assets, aQ]);

  const groupedAssets = useMemo(() => {
    const buckets = {};
    KIND_GROUP_VALUES.forEach((g) => { buckets[g] = []; });
    filteredAssets.forEach((a) => {
      const key = buckets[a.kind] !== undefined ? a.kind : "altro";
      buckets[key].push(a);
    });
    return KIND_GROUP_VALUES
      .map((value) => ({ value, label: t(`suppliers.kind_${value}`), items: buckets[value] }))
      .filter((g) => g.items.length > 0);
  }, [filteredAssets, t]);

  // Collapse/expand per ogni gruppo. Default: tutti CHIUSI (mostra solo "{N} file") —
  // con cataloghi grossi (Poliform 30+ file) la lista intera diventava ingestibile.
  // Quando l'utente fa una ricerca testuale (`aQ` non vuoto) apriamo automaticamente
  // i gruppi che hanno match, così vede subito i risultati.
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set(KIND_GROUP_VALUES));
  const hasActiveSearch = (aQ || "").trim().length > 0;
  const isGroupOpen = (val) => hasActiveSearch || !collapsedGroups.has(val);
  function toggleGroup(val) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  }

  const showListinoGate = !supplier.has_access;

  // Aumento listino: badge "+X% dal DD/MM/YYYY" sotto agli asset listino se il fornitore
  // ha impostato l'aumento (`price_increase_pct` > 0 e `price_increase_date`) e l'admin
  // NON ha escluso questo singolo file (`exclude_price_increase`).
  const priceIncreasePct = Number(supplier.price_increase_pct || 0);
  const priceIncreaseDate = supplier.price_increase_date || "";
  const priceIncreaseNewListino = Boolean(supplier.price_increase_new_listino);
  const supplierHasIncrease = (priceIncreasePct > 0 || priceIncreaseNewListino) && Boolean(priceIncreaseDate);
  let priceIncreaseEffectiveLabel = "";
  if (supplierHasIncrease) {
    try {
      const dt = new Date(priceIncreaseDate);
      if (!isNaN(dt.getTime())) {
        priceIncreaseEffectiveLabel = dt.toLocaleDateString(dateLocale);
      }
    } catch { /* ignore */ }
  }

  return (
    <div data-testid="asset-list-pane">
      <MagicSearch
        supplierId={supplier.id}
        assets={assets}
        onOpenAtPage={handleOpenAtPage}
        onOpenFile={handleOpenFile}
        initialQuery={initialMagicQuery}
      />
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="font-display text-xl text-ink">{t("suppliers.asset_list_title")}</div>
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={14} />
          <input
            value={aQ}
            onChange={(e) => setAQ(e.target.value)}
            placeholder={t("suppliers.search_files")}
            className="w-full pl-9 pr-3 py-2 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
            data-testid="asset-search"
          />
        </div>
      </div>

      {(freshCounts.added > 0 || freshCounts.updated > 0) && (
        <div
          className="bg-gold/10 border border-gold/40 rounded-sm p-4 mb-4 flex items-start gap-3"
          data-testid="asset-news-banner"
        >
          <Sparkles size={16} className="text-gold mt-0.5 shrink-0" />
          <div className="text-sm text-ink leading-relaxed">
            <span className="font-medium">
              {[
                freshCounts.added > 0 ? t("suppliers.asset_news_new", { count: freshCounts.added }) : null,
                freshCounts.updated > 0 ? t("suppliers.asset_news_updated", { count: freshCounts.updated }) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>{" "}
            {t("suppliers.asset_news_since", { date: new Date(newSince).toLocaleDateString(dateLocale) })}
          </div>
        </div>
      )}

      {showListinoGate && (
        <div
          className="bg-white border border-ink/10 rounded-sm p-4 mb-4 flex items-center justify-between gap-3"
          data-testid="trade-conditions-banner"
        >
          <div className="flex items-start gap-3 min-w-0">
            <Tag size={16} className="text-gold mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold">{t("suppliers.trade_terms_pre")}</div>
              <p className="mt-0.5 text-sm text-ink leading-relaxed">
                {t("suppliers.trade_terms_body")}
              </p>
            </div>
          </div>
          {supplier.pending_request ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-cream2 text-slate1 text-[11px] tracking-[0.15em] uppercase shrink-0">
              <MailQuestion size={12} /> {t("suppliers.request_pending")}
            </span>
          ) : (
            <button
              onClick={() => onRequestAccess(supplier)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift shrink-0"
              data-testid="listino-request-access"
            >
              <Tag size={12} /> {t("suppliers.trade_terms_request_btn")}
            </button>
          )}
        </div>
      )}

      {filteredAssets.length === 0 && (
        <div className="bg-white border border-ink/10 rounded-sm p-6 text-sm text-slate1 text-center">
          {t("suppliers.asset_none")}
        </div>
      )}

      <div className="space-y-4">
        {groupedAssets.map((group) => {
          const open = isGroupOpen(group.value);
          return (
          <div key={group.value} data-testid={`asset-group-${group.value}`} className="bg-white border border-ink/10 rounded-sm">
            <button
              type="button"
              onClick={() => toggleGroup(group.value)}
              className={`w-full px-4 py-3 ${open ? "border-b border-ink/10" : ""} flex items-center justify-between gap-3 hover:bg-cream2/40 transition-colors`}
              aria-expanded={open}
              data-testid={`asset-group-toggle-${group.value}`}
            >
              <div className="flex items-center gap-2">
                {open ? <ChevronDown size={14} className="text-slate1" /> : <ChevronRight size={14} className="text-slate1" />}
                <Folder size={14} className="text-slate1" />
                <div className="text-[11px] tracking-[0.2em] uppercase text-ink">{group.label}</div>
              </div>
              <div className="text-[11px] tracking-[0.15em] uppercase text-slate1">
                {t("suppliers.asset_count_files", { count: group.items.length })}
              </div>
            </button>
            {open && (
            <div className="divide-y divide-ink/10">
              {group.items.map((a) => {
                const isPdfA = (a.content_type || "").toLowerCase() === "application/pdf"
                  || (a.filename || "").toLowerCase().endsWith(".pdf");
                return (
                <div key={a.id} className="p-4 flex items-center gap-4" data-testid={`asset-${a.id}`}>
                  {a.content_type === "external/url" ? (
                    <Link2 size={16} className="text-gold shrink-0" />
                  ) : a.content_type === "gdrive/folder" ? (
                    <Folder size={16} className="text-gold shrink-0" />
                  ) : isPdfA && isPreviewable(a) ? (
                    <AssetCoverThumbnail
                      assetId={a.id}
                      testid={`asset-cover-${a.id}`}
                      title={`${t("suppliers.asset_preview")} · ${a.filename || a.title || ""}`}
                      onClick={() => onPreview(a)}
                      size="md"
                    />
                  ) : (
                    <FileText size={16} className="text-slate1 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="text-ink truncate">{a.title}</div>
                      {isNewAsset(a) && (
                        <span
                          className="shrink-0 px-1.5 py-0.5 bg-gold text-cream text-[9px] tracking-[0.2em] uppercase rounded-sm"
                          data-testid={`asset-new-badge-${a.id}`}
                        >
                          {t("suppliers.asset_badge_new")}
                        </span>
                      )}
                      {isUpdatedAsset(a) && (
                        <span
                          className="shrink-0 px-1.5 py-0.5 bg-ink text-cream text-[9px] tracking-[0.2em] uppercase rounded-sm"
                          data-testid={`asset-updated-badge-${a.id}`}
                        >
                          {t("suppliers.asset_badge_updated")}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate1">
                      {a.size > 0 && <>{(a.size / 1024 / 1024).toFixed(2)} MB</>}
                      {a.content_type === "external/url" && <> · {t("suppliers.asset_external_link")}</>}
                      {a.content_type === "gdrive/folder" && (
                        <>cartella Drive · {a.gdrive_files_count || 0} file</>
                      )}
                    </div>
                    {a.created_at && (
                      <div className="text-[11px] text-slate1/80 mt-0.5">
                        {t("suppliers.asset_uploaded_on", { date: new Date(a.created_at).toLocaleDateString(dateLocale) })}
                      </div>
                    )}
                    {a.kind === "listino" && supplierHasIncrease && !a.exclude_price_increase && (
                      <div
                        className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase border border-rose-300 bg-rose-50 text-rose-700 px-2 py-1 rounded-sm"
                        data-testid={`asset-price-increase-badge-${a.id}`}
                        title={priceIncreaseNewListino
                          ? t("suppliers.price_increase_new_tooltip", { date: priceIncreaseEffectiveLabel })
                          : t("suppliers.price_increase_tooltip", { pct: priceIncreasePct, date: priceIncreaseEffectiveLabel })}
                      >
                        <TrendingUp size={11} />
                        {priceIncreaseNewListino
                          ? t("suppliers.price_increase_new_badge", { date: priceIncreaseEffectiveLabel })
                          : t("suppliers.price_increase_badge", { pct: priceIncreasePct, date: priceIncreaseEffectiveLabel })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => onDownload(a)}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-ink/20 text-ink text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink hover:text-cream transition-colors"
                    data-testid={`asset-dl-${a.id}`}
                  >
                    <Download size={13} /> {(a.content_type === "external/url" || a.content_type === "gdrive/folder") ? t("suppliers.asset_open") : t("suppliers.asset_download")}
                  </button>
                  {isPreviewable(a) && (
                    <button
                      onClick={() => onPreview(a)}
                      disabled={previewLoading}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-ink text-cream text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50 transition-colors"
                      data-testid={`asset-preview-${a.id}`}
                    >
                      <Eye size={13} /> {t("suppliers.asset_preview")}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Suppliers() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dateLocale = i18n.language.startsWith("it") ? "it-IT" : "en-GB";
  const [searchParams, setSearchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [assets, setAssets] = useState([]);
  const [aQ, setAQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [mobileTab, setMobileTab] = useState("assets"); // assets | profile
  const [newSince, setNewSince] = useState(null);
  // Disclaimer modal state — non-admin must accept before each download
  const [disclaimerAsset, setDisclaimerAsset] = useState(null);
  // Deep-link: ?supplier=<id>&magic=<term> dalla pagina AdminStats (Top10) — auto-apre
  // il supplier e precarica la query nel pannello Magic Search.
  const deepLinkSupplierId = searchParams.get("supplier") || "";
  const deepLinkMagic = searchParams.get("magic") || "";
  const [magicInitialQuery, setMagicInitialQuery] = useState("");

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  // Quando suppliers è caricato e c'è un deep-link, apri direttamente quel supplier
  // (una volta sola: dopo aver aperto puliamo i query params per non riaprire).
  useEffect(() => {
    if (!deepLinkSupplierId || suppliers.length === 0) return;
    const target = suppliers.find((s) => s.id === deepLinkSupplierId);
    if (!target) {
      toast.error(t("suppliers.deep_link_not_found", { defaultValue: "Fornitore non trovato" }));
      setSearchParams({}, { replace: true });
      return;
    }
    setMagicInitialQuery(deepLinkMagic);
    openSupplier(target);
    setSearchParams({}, { replace: true });
  }, [suppliers, deepLinkSupplierId, deepLinkMagic]);

  const filtered = useMemo(() => {
    if (!q) return suppliers;
    const k = q.toLowerCase();
    return suppliers.filter(
      (s) =>
        s.name.toLowerCase().includes(k) ||
        (s.description || "").toLowerCase().includes(k)
    );
  }, [suppliers, q]);

  async function openSupplier(s) {
    setOpen(s);
    setMobileTab("assets");
    setNewSince(null);
    try {
      const [assetsRes, seenRes] = await Promise.all([
        api.get(`/suppliers/${s.id}/assets`),
        api.post(`/suppliers/${s.id}/assets/seen`).catch(() => ({ data: { new_since: null } })),
      ]);
      setAssets(assetsRes.data);
      setNewSince(seenRes.data?.new_since || null);
    } catch {
      toast.error(t("suppliers.assets_load_error"));
    }
  }

  async function performDownload(a) {
    try {
      if (a.content_type === "external/url" || a.content_type === "gdrive/folder") {
        const { data } = await api.get(`/assets/${a.id}/download`);
        if (data.redirect) window.open(data.redirect, "_blank");
        return;
      }
      const r = await api.get(`/assets/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("suppliers.asset_download_error"));
    }
  }

  async function downloadAsset(a) {
    // Admin: bypass disclaimer (può fare tutto)
    if (isAdmin) {
      return performDownload(a);
    }
    // Link esterni / Google Drive: nessun materiale riservato locale, niente disclaimer
    if (a.content_type === "external/url" || a.content_type === "gdrive/folder") {
      return performDownload(a);
    }
    // Documento riservato su nostro storage → mostra modale di accettazione
    setDisclaimerAsset(a);
  }

  async function previewAsset(a, page, searchTerm) {
    if (!isPreviewable(a)) return;
    const ct = (a.content_type || "").toLowerCase();
    const isPdf = ct === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");

    // Per i PDF apriamo il NOSTRO viewer (public/pdfjs/viewer.html) basato su PDF.js.
    // Motivo: il viewer PDF nativo del browser ignora `#page=N&search=...` quando
    // l'utente ha estensioni come Adobe Acrobat installate (intercettano l'URL e
    // strippano il fragment). Il nostro viewer riceve i parametri via query string
    // (?file=&page=&search=) che NESSUNA estensione intercetta, garantisce il page
    // jump immediato e l'highlight in giallo della parola cercata cross-browser.
    if (isPdf) {
      const fileUrl = `${API}/assets/${a.id}/preview`;
      const qp = new URLSearchParams();
      qp.set("file", fileUrl);
      if (page && Number.isFinite(Number(page))) qp.set("page", String(page));
      if (searchTerm && typeof searchTerm === "string") {
        const firstToken = searchTerm.trim().split(/\s+/)[0];
        if (firstToken) qp.set("search", firstToken);
      }
      // Admin: abilita il download dal viewer; non-admin: i bottoni "Scarica"
      // e "Pagina" vengono nascosti dal viewer.html in base a questo flag.
      if (isAdmin) qp.set("admin", "1");
      qp.set("from", "fornitori");
      window.open(`/pdfjs/viewer.html?${qp.toString()}`, "_blank");
      return;
    }

    let pdfTab = null;
    if (isPdf) {
      pdfTab = window.open("about:blank", "_blank");
      if (!pdfTab) {
        toast.error(t("suppliers.asset_preview_blocked"));
        return;
      }
    }
    setPreviewLoading(true);
    // Su file molto grandi (>20 MB) mostriamo un toast per spiegare il caricamento
    const sizeMB = (a.size || 0) / (1024 * 1024);
    const loadingToastId = sizeMB > 20
      ? toast.loading(t("suppliers.asset_preview_loading_large", { mb: Math.round(sizeMB) }))
      : null;
    try {
      const r = await api.get(`/assets/${a.id}/preview`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: a.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (isPdf && pdfTab) {
        pdfTab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        setPreview({ url, name: a.title || a.filename, isPdf: false, isImage: ct.startsWith("image/") });
      }
    } catch (err) {
      if (pdfTab) {
        try { pdfTab.close(); } catch (_) { /* ignore */ }
      }
      toast.error(err.response?.data?.detail || t("suppliers.asset_preview_error"));
    } finally {
      setPreviewLoading(false);
      if (loadingToastId !== null) toast.dismiss(loadingToastId);
    }
  }

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function requestAccess(supplier) {
    try {
      const { data } = await api.post(`/suppliers/${supplier.id}/discount-request`);
      if (data.status === "already_set") toast.success(t("suppliers.request_success_already"));
      else if (data.status === "pending") toast.info(t("suppliers.request_already_pending"));
      else toast.success(t("suppliers.request_success_new"));
      const r = await api.get("/suppliers");
      setSuppliers(r.data);
      if (open && open.id === supplier.id) {
        const updated = r.data.find((s) => s.id === supplier.id);
        if (updated) {
          setOpen(updated);
          const a = await api.get(`/suppliers/${supplier.id}/assets`);
          setAssets(a.data);
        }
      }
    } catch {
      toast.error(t("suppliers.request_error"));
    }
  }

  return (
    <div className="pt-28 pb-20 bg-cream min-h-[100svh]" data-testid="suppliers-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10">
        {!open ? (
          <>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("suppliers.section")}</div>
                <h1 className="mt-2 font-display text-4xl lg:text-5xl font-light text-ink">{t("suppliers.title")}</h1>
                <p className="mt-3 text-slate1 max-w-2xl">
                  {t("suppliers.subtitle")}
                  <span className="relative inline-block group align-middle ml-2" data-testid="suppliers-how-tooltip">
                    <Info size={15} className="text-gold cursor-help inline" />
                    <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 max-w-[85vw] bg-ink text-cream text-xs leading-relaxed rounded-sm px-4 py-3 opacity-0 group-hover:opacity-100 transition-opacity z-40 shadow-xl">
                      <strong className="block mb-1 text-gold text-[10px] tracking-[0.2em] uppercase">{t("suppliers.how_tooltip_title")}</strong>
                      {t("suppliers.how_tooltip_body")}
                    </span>
                  </span>
                </p>
              </div>
              <div className="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col items-stretch gap-3 lg:items-end">
                {user && user.role !== "admin" && ["trader", "studio"].includes(user.account_type) && (
                  <Link
                    to="/magic-search"
                    data-testid="magic-search-cta"
                    className="group relative inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm bg-gradient-to-r from-gold to-amber-400 text-ink text-xs tracking-[0.25em] uppercase font-semibold shadow-lg hover:shadow-gold/40 hover:brightness-105 transition-all overflow-hidden"
                    title="Cerca in tutti i listini con la ricerca intelligente"
                  >
                    <Wand2 size={16} className="shrink-0" />
                    <span>Magic Search</span>
                    <Sparkles size={13} className="shrink-0 opacity-80" />
                  </Link>
                )}
                <div className="relative w-full lg:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={16} />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t("suppliers.search_placeholder")}
                    className="w-full pl-10 pr-4 py-3 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
                    data-testid="suppliers-search"
                  />
                </div>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="suppliers-grid">
              {filtered.length === 0 && (
                <div className="text-sm text-slate1 col-span-full text-center py-10">{t("suppliers.none_now")}</div>
              )}
              {filtered.map((s) => (
                <button
                  key={s.id}
                  onClick={() => openSupplier(s)}
                  className="text-left bg-white border border-ink/10 p-6 rounded-sm hover-lift"
                  data-testid={`sup-card-${s.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="relative">
                        <Folder className="text-ink" size={22} />
                        {s.has_news && (
                          <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" data-testid={`sup-news-dot-${s.id}`}>
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold"></span>
                          </span>
                        )}
                      </div>
                      {s.has_news && (
                        <span className="text-[10px] tracking-[0.2em] uppercase text-gold font-medium">
                          {t("suppliers.news_dot")}
                        </span>
                      )}
                    </div>
                    {s.has_access ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-gold">
                        <CheckCircle2 size={11} /> {t("suppliers.access_active")}
                      </span>
                    ) : s.pending_request ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate1">
                        <MailQuestion size={11} /> {t("suppliers.pending")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate1">
                        <Tag size={11} /> {t("suppliers.trade_terms_locked")}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 font-display text-xl text-ink">{s.name}</div>
                  {s.description && <p className="mt-2 text-sm text-slate1 line-clamp-2">{s.description}</p>}
                  <div className="mt-5 flex flex-wrap gap-2 text-[11px] tracking-[0.15em] uppercase">
                    {s.lead_time_days > 0 && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-cream2 text-ink rounded-sm">
                        <Clock size={11} /> {t("suppliers.lead_time_days", { days: s.lead_time_days })}
                      </span>
                    )}
                    {(() => {
                      const od = getOrderDeadlineAlert(s);
                      if (!od) return null;
                      const cls = {
                        today:    "bg-rose-100 text-rose-800 border border-rose-300 animate-pulse",
                        imminent: "bg-rose-100 text-rose-800 border border-rose-300 animate-pulse",
                        future:   "bg-blue-50 text-blue-800 border border-blue-200",
                        past:     "bg-slate-100 text-slate-600 border border-slate-200",
                      };
                      const dateStr = od.date.toLocaleDateString(dateLocale);
                      const lbl =
                        od.state === "today" ? t("suppliers.deadline_today")
                        : od.state === "imminent" ? (od.days === 1 ? t("suppliers.deadline_tomorrow") : t("suppliers.deadline_days", { count: od.days }))
                        : od.state === "future" ? t("suppliers.deadline_by_date", { date: dateStr })
                        : t("suppliers.deadline_past");
                      const Icon = (od.state === "today" || od.state === "imminent") ? AlertTriangle : CalendarClock;
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm normal-case tracking-normal ${cls[od.state]}`}
                          data-testid={`sup-deadline-${s.id}`}
                          data-state={od.state}
                          title={t("suppliers.deadline_title_hover", { date: dateStr })}
                        >
                          <Icon size={11} /> {lbl}
                        </span>
                      );
                    })()}
                    {s.website && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-cream2 text-slate1 rounded-sm">
                        <Globe size={11} /> {t("suppliers.website_lbl")}
                      </span>
                    )}
                    {s.has_access && s.discount_pct > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm bg-gold text-cream">
                        <Tag size={11} />
                        {s.discount_chain && s.discount_chain.length > 1
                          ? t("suppliers.discount_chain_reserved", { chain: s.discount_chain.filter((v) => Number(v) > 0).join(" + "), percent: s.discount_pct })
                          : t("suppliers.discount_reserved", { percent: s.discount_pct })}
                      </span>
                    ) : s.pending_request ? null : (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          requestAccess(s);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                            requestAccess(s);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-ink/20 text-ink hover:bg-ink hover:text-cream transition-colors cursor-pointer"
                        data-testid={`sup-request-${s.id}`}
                      >
                        <Tag size={11} /> {t("suppliers.request_access")}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                // La visita è stata registrata: spegni il pallino novità per questo fornitore
                setSuppliers((prev) => prev.map((x) => (x.id === open.id ? { ...x, has_news: false } : x)));
                setOpen(null);
                setAssets([]);
                setAQ("");
                setMagicInitialQuery("");
              }}
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink mb-5"
              data-testid="sup-back"
            >
              <ChevronLeft size={14} /> {t("suppliers.all_suppliers_back")}
            </button>

            {/* Mobile tabs: assets / profile */}
            <div className="lg:hidden mb-4 inline-flex border border-ink/20 rounded-sm overflow-hidden" data-testid="supplier-mobile-tabs">
              <button
                onClick={() => setMobileTab("assets")}
                className={`px-5 py-2 text-[11px] tracking-[0.2em] uppercase ${mobileTab === "assets" ? "bg-ink text-cream" : "bg-white text-slate1"}`}
                data-testid="supplier-tab-assets"
              >
                {t("suppliers.tab_assets")}
              </button>
              <button
                onClick={() => setMobileTab("profile")}
                className={`px-5 py-2 text-[11px] tracking-[0.2em] uppercase ${mobileTab === "profile" ? "bg-ink text-cream" : "bg-white text-slate1"}`}
                data-testid="supplier-tab-profile"
              >
                {t("suppliers.tab_profile")}
              </button>
            </div>

            {/* Split view on lg+, tabbed on mobile */}
            <div className="lg:grid lg:grid-cols-2 lg:gap-8">
              {/* Left: Asset list */}
              <div className={`${mobileTab === "assets" ? "block" : "hidden"} lg:block`}>
                <AssetList
                  supplier={open}
                  assets={assets}
                  aQ={aQ}
                  setAQ={setAQ}
                  onRequestAccess={requestAccess}
                  onPreview={previewAsset}
                  onDownload={downloadAsset}
                  previewLoading={previewLoading}
                  newSince={newSince}
                  initialMagicQuery={magicInitialQuery}
                />
              </div>

              {/* Right: Supplier profile (read-only) */}
              <div className={`${mobileTab === "profile" ? "block" : "hidden"} lg:block`}>
                <div className="bg-white border border-ink/10 rounded-sm p-6 lg:sticky lg:top-28">
                  <SupplierProfilePanel supplier={open} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex flex-col"
          onClick={closePreview}
          data-testid="asset-preview-modal"
        >
          <div className="flex items-center justify-between px-6 py-3 bg-ink text-cream" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm tracking-[0.15em] uppercase truncate">{preview.name}</div>
            <button
              onClick={closePreview}
              className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm"
              data-testid="asset-preview-close"
              aria-label={t("suppliers.asset_close_preview")}
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 bg-cream2" onClick={(e) => e.stopPropagation()}>
            {preview.isPdf ? (
              <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" data-testid="asset-preview-iframe" />
            ) : preview.isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate1 text-sm">
                {t("suppliers.asset_preview_unsupported")}
              </div>
            )}
          </div>
        </div>
      )}

      <DownloadDisclaimerModal
        open={!!disclaimerAsset}
        assetId={disclaimerAsset?.id}
        fileLabel={disclaimerAsset?.title || disclaimerAsset?.filename || ""}
        onAccept={() => {
          const a = disclaimerAsset;
          setDisclaimerAsset(null);
          if (a) performDownload(a);
        }}
        onClose={() => setDisclaimerAsset(null)}
      />
    </div>
  );
}
